import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  Row,
  Col,
  Form,
  Pagination,
  Alert,
  Table,
} from 'react-bootstrap';
import Select from 'react-select';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import moment from 'moment';

import Loading from '~/Components/Loading';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  LIMITE_LOTE_BOLETOS_MONITORA,
  PARAM_DIAS_MONITORA_FALLBACK,
} from '~/constant';
import {
  monitoraFamilias,
  monitoraAtivos,
  monitoraAtivosIds,
  monitoraParamDias,
  monitoraAtivar,
} from '~/Services/Service';

const PAGE_SIZE_ATIVOS = 20;
const CAP_SELECIONAR_TODOS = 1000;

const hojeISO = () => moment().format('YYYY-MM-DD');

const calcularDataPadrao = (diasMonitora) => {
  const dias = Number.isInteger(diasMonitora) && diasMonitora > 0
    ? diasMonitora
    : PARAM_DIAS_MONITORA_FALLBACK;
  return moment().add(dias, 'days').format('YYYY-MM-DD');
};

const schemaMonitoramento = Yup.object({
  familia: Yup.number()
    .typeError('Selecione um empreendimento')
    .required('Selecione um empreendimento'),
  ate: Yup.date()
    .typeError('Data inválida')
    .required('Data obrigatória')
    .min(moment().startOf('day').toDate(), 'Data deve ser hoje ou futura'),
  observacao: Yup.string().max(500, 'Máximo 500 caracteres'),
});

const ModalNovoMonitoramento = ({
  show,
  ge,
  cliente,
  clienteLabel,
  onHide,
  onSucesso,
}) => {
  const [familiasOpts, setFamiliasOpts] = useState([]);
  const [familiaSelecionada, setFamiliaSelecionada] = useState(null);
  const [loadingFamilias, setLoadingFamilias] = useState(false);

  const [ativos, setAtivos] = useState([]);
  const [totalAtivos, setTotalAtivos] = useState(0);
  const [pageAtivos, setPageAtivos] = useState(1);
  const [loadingAtivos, setLoadingAtivos] = useState(false);

  const [boletosSelecionados, setBoletosSelecionados] = useState(new Set());
  const [todosFiltroSelecionados, setTodosFiltroSelecionados] = useState(false);
  const [carregandoTodos, setCarregandoTodos] = useState(false);

  const [diasMonitora, setDiasMonitora] = useState(PARAM_DIAS_MONITORA_FALLBACK);
  const [paramCarregado, setParamCarregado] = useState(false);

  const [enviando, setEnviando] = useState(false);

  // Carregar Param.DiasMonitora (com fallback)
  useEffect(() => {
    let ativo = true;
    monitoraParamDias()
      .then((res) => {
        if (!ativo) return;
        const dias =
          res && Number.isInteger(res.dias_monitora)
            ? res.dias_monitora
            : PARAM_DIAS_MONITORA_FALLBACK;
        setDiasMonitora(dias);
      })
      .catch(() => {
        if (!ativo) return;
        setDiasMonitora(PARAM_DIAS_MONITORA_FALLBACK);
      })
      .finally(() => {
        if (ativo) setParamCarregado(true);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Carregar famílias quando GE/Cliente mudam
  useEffect(() => {
    if (!ge || !cliente) {
      setFamiliasOpts([]);
      return undefined;
    }
    let ativo = true;
    setLoadingFamilias(true);
    monitoraFamilias(ge, cliente)
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        if (lista.length === 0) {
          toast.warn('Cliente não possui nenhuma venda.');
        }
        const opts = lista.map((f) => ({
          value: f.codigo_familia,
          label: `${f.codigo_familia} - ${f.nome_da_familia || ''}`.trim(),
        }));
        setFamiliasOpts(opts);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setFamiliasOpts([]);
      })
      .finally(() => {
        if (ativo) setLoadingFamilias(false);
      });
    return () => {
      ativo = false;
    };
  }, [ge, cliente]);

  // Carregar ativos quando família/page mudam
  useEffect(() => {
    if (!ge || !cliente || !familiaSelecionada) {
      setAtivos([]);
      setTotalAtivos(0);
      return undefined;
    }
    let ativo = true;
    setLoadingAtivos(true);
    monitoraAtivos(ge, cliente, familiaSelecionada.value, pageAtivos, PAGE_SIZE_ATIVOS)
      .then((res) => {
        if (!ativo) return;
        const items = (res && Array.isArray(res.items)) ? res.items : [];
        setAtivos(items);
        setTotalAtivos((res && res.total) || 0);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setAtivos([]);
        setTotalAtivos(0);
      })
      .finally(() => {
        if (ativo) setLoadingAtivos(false);
      });
    return () => {
      ativo = false;
    };
  }, [ge, cliente, familiaSelecionada, pageAtivos]);

  // Reset selecionados ao trocar família
  useEffect(() => {
    setBoletosSelecionados(new Set());
    setTodosFiltroSelecionados(false);
    setPageAtivos(1);
  }, [familiaSelecionada]);

  const totalPaginasAtivos = Math.max(1, Math.ceil(totalAtivos / PAGE_SIZE_ATIVOS));

  const todosDaPaginaMarcados = useMemo(() => {
    if (ativos.length === 0) return false;
    return ativos.every((a) => boletosSelecionados.has(a.codigo_boleto));
  }, [ativos, boletosSelecionados]);

  const toggleBoleto = (codigoBoleto) => {
    setBoletosSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(codigoBoleto)) {
        next.delete(codigoBoleto);
      } else {
        next.add(codigoBoleto);
      }
      return next;
    });
    setTodosFiltroSelecionados(false);
  };

  const toggleTodosDaPagina = () => {
    setBoletosSelecionados((prev) => {
      const next = new Set(prev);
      if (todosDaPaginaMarcados) {
        ativos.forEach((a) => next.delete(a.codigo_boleto));
      } else {
        ativos.forEach((a) => next.add(a.codigo_boleto));
      }
      return next;
    });
    setTodosFiltroSelecionados(false);
  };

  const selecionarTodosDoFiltro = () => {
    if (!ge || !cliente || !familiaSelecionada) return;
    if (totalAtivos > CAP_SELECIONAR_TODOS) {
      toast.warn(
        `Apenas os primeiros ${CAP_SELECIONAR_TODOS} podem ser selecionados de uma vez.`
      );
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Vai selecionar ${Math.min(totalAtivos, CAP_SELECIONAR_TODOS)} boletos. Continuar?`
    );
    if (!ok) return;
    setCarregandoTodos(true);
    monitoraAtivosIds(ge, cliente, familiaSelecionada.value)
      .then((res) => {
        const ids = (res && Array.isArray(res.ids)) ? res.ids : [];
        setBoletosSelecionados(new Set(ids));
        setTodosFiltroSelecionados(true);
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        setCarregandoTodos(false);
      });
  };

  const limparSelecao = () => {
    setBoletosSelecionados(new Set());
    setTodosFiltroSelecionados(false);
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const boletos = Array.from(boletosSelecionados);
    if (boletos.length < 1) {
      toast.error('Selecione pelo menos 1 boleto.');
      setSubmitting(false);
      return;
    }
    if (boletos.length > LIMITE_LOTE_BOLETOS_MONITORA) {
      toast.error(`Máximo ${LIMITE_LOTE_BOLETOS_MONITORA} boletos por monitoramento.`);
      setSubmitting(false);
      return;
    }
    const payload = {
      ge,
      cliente,
      familia: Number(values.familia),
      boletos,
      ate: values.ate,
      observacao: values.observacao || '',
    };
    setEnviando(true);
    monitoraAtivar(payload)
      .then((res) => {
        const sucessos = (res && Array.isArray(res.sucessos)) ? res.sucessos : [];
        const falhas = (res && Array.isArray(res.falhas)) ? res.falhas : [];
        if (falhas.length === 0) {
          toast.success(`${sucessos.length} boletos ativados.`);
        } else if (sucessos.length === 0) {
          toast.error(
            `Nenhum boleto ativado:\n${falhas.map((f) => f.motivo).join('\n')}`
          );
        } else {
          toast.warning(
            `${sucessos.length} ativados, ${falhas.length} com erro:\n${falhas
              .map((f) => f.motivo)
              .join('\n')}`
          );
        }
        if (onSucesso) onSucesso();
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        setEnviando(false);
        setSubmitting(false);
      });
  };

  const renderPaginacaoAtivos = () => {
    if (totalAtivos === 0 || totalPaginasAtivos <= 1) return null;
    const items = [];
    const maxBotoes = 5;
    let inicio = Math.max(1, pageAtivos - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginasAtivos, inicio + maxBotoes - 1);
    if (fim - inicio + 1 < maxBotoes) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }
    items.push(
      <Pagination.First
        key="first"
        disabled={pageAtivos === 1}
        onClick={() => setPageAtivos(1)}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={pageAtivos === 1}
        onClick={() => setPageAtivos(pageAtivos - 1)}
      />
    );
    for (let n = inicio; n <= fim; n += 1) {
      items.push(
        <Pagination.Item
          key={`ap-${n}`}
          active={n === pageAtivos}
          onClick={() => setPageAtivos(n)}
        >
          {n}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        disabled={pageAtivos === totalPaginasAtivos}
        onClick={() => setPageAtivos(pageAtivos + 1)}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        disabled={pageAtivos === totalPaginasAtivos}
        onClick={() => setPageAtivos(totalPaginasAtivos)}
      />
    );
    return (
      <Pagination size="sm" className="mb-0">
        {items}
      </Pagination>
    );
  };

  if (!paramCarregado) {
    return (
      <Modal show={show} size="lg" onHide={onHide}>
        <Modal.Body>
          <Loading />
        </Modal.Body>
      </Modal>
    );
  }

  const valoresIniciais = {
    familia: '',
    ate: calcularDataPadrao(diasMonitora),
    observacao: '',
  };

  return (
    <Modal show={show} size="lg" onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Novo Monitoramento {clienteLabel ? `— ${clienteLabel}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={schemaMonitoramento}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: formikSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form noValidate onSubmit={formikSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6} sm={12} className="mb-2">
                  <Form.Label className="small mb-1">Empreendimento</Form.Label>
                  <Select
                    styles={selectStylesSm}
                    placeholder="Selecione..."
                    options={familiasOpts}
                    value={
                      familiaSelecionada
                        ? familiasOpts.find(
                            (o) => o.value === familiaSelecionada.value
                          ) || null
                        : null
                    }
                    onChange={(opt) => {
                      setFamiliaSelecionada(opt);
                      setFieldValue('familia', opt ? opt.value : '');
                    }}
                    isLoading={loadingFamilias}
                    isClearable
                    noOptionsMessage={() =>
                      loadingFamilias ? 'Carregando...' : 'Nenhum empreendimento'
                    }
                  />
                  {touched.familia && errors.familia && (
                    <div className="text-danger small mt-1">{errors.familia}</div>
                  )}
                </Col>
                <Col md={3} sm={6} className="mb-2">
                  <Form.Label className="small mb-1">Monitorar até</Form.Label>
                  <Form.Control
                    type="date"
                    name="ate"
                    value={values.ate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min={hojeISO()}
                    size="sm"
                    isInvalid={touched.ate && !!errors.ate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ate}
                  </Form.Control.Feedback>
                </Col>
                <Col md={3} sm={6} className="mb-2">
                  <Form.Label className="small mb-1">Selecionados</Form.Label>
                  <div>
                    <strong>{boletosSelecionados.size}</strong>
                    <span className="text-muted ml-1">
                      / {LIMITE_LOTE_BOLETOS_MONITORA} máx.
                    </span>
                    {boletosSelecionados.size > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ml-2"
                        onClick={limparSelecao}
                      >
                        limpar
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {familiaSelecionada && (
                <>
                  <hr className="my-2" />
                  <div className="d-flex align-items-center mb-2">
                    <Form.Check
                      type="checkbox"
                      id="chkTodosPagina"
                      label="Selecionar todos da página"
                      checked={todosDaPaginaMarcados}
                      onChange={toggleTodosDaPagina}
                      disabled={ativos.length === 0}
                    />
                    <small className="text-muted ml-3">
                      {totalAtivos} imóveis no filtro
                    </small>
                  </div>

                  {todosDaPaginaMarcados &&
                    !todosFiltroSelecionados &&
                    totalAtivos > ativos.length && (
                      <Alert variant="info" className="py-2 mb-2 small">
                        {boletosSelecionados.size} de {totalAtivos} selecionados.{' '}
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 align-baseline"
                          onClick={selecionarTodosDoFiltro}
                          disabled={carregandoTodos}
                        >
                          {carregandoTodos
                            ? 'Carregando...'
                            : `Clique aqui para selecionar todos os ${totalAtivos}`}
                        </Button>
                      </Alert>
                    )}

                  {todosFiltroSelecionados && (
                    <Alert variant="success" className="py-2 mb-2 small">
                      Todos os {boletosSelecionados.size} boletos do filtro
                      selecionados.
                    </Alert>
                  )}

                  {totalAtivos > CAP_SELECIONAR_TODOS && (
                    <Alert variant="warning" className="py-2 mb-2 small">
                      Apenas os primeiros {CAP_SELECIONAR_TODOS} podem ser
                      selecionados de uma vez.
                    </Alert>
                  )}

                  {loadingAtivos ? (
                    <Loading />
                  ) : (
                    <Table striped bordered hover size="sm" responsive>
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>&nbsp;</th>
                          <th>Bol.</th>
                          <th>QD</th>
                          <th>LT</th>
                          <th>Imóvel</th>
                          <th>Nome</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ativos.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">
                              Nenhum imóvel disponível para o cliente nesse
                              empreendimento.
                            </td>
                          </tr>
                        ) : (
                          ativos.map((a) => {
                            const checked = boletosSelecionados.has(
                              a.codigo_boleto
                            );
                            return (
                              <tr key={`${a.codigo_ativo}-${a.codigo_boleto}`}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    id={`chk-bol-${a.codigo_boleto}`}
                                    checked={checked}
                                    onChange={() =>
                                      toggleBoleto(a.codigo_boleto)
                                    }
                                  />
                                </td>
                                <td>{a.codigo_boleto}</td>
                                <td>{a.mapa1 || '-'}</td>
                                <td>{a.mapa2 || '-'}</td>
                                <td>{a.codigo_ativo}</td>
                                <td>{a.nome || '-'}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  )}
                  <div className="d-flex justify-content-end">
                    {renderPaginacaoAtivos()}
                  </div>
                </>
              )}

              <hr className="my-2" />
              <Form.Group>
                <Form.Label className="small mb-1">Observação</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="observacao"
                  value={values.observacao}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={500}
                  isInvalid={touched.observacao && !!errors.observacao}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.observacao}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {values.observacao ? values.observacao.length : 0}/500
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={onHide}
                disabled={isSubmitting || enviando}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  isSubmitting ||
                  enviando ||
                  boletosSelecionados.size === 0 ||
                  !familiaSelecionada
                }
              >
                {enviando ? 'Salvando...' : 'OK'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ModalNovoMonitoramento;
